/**
 * FinancialSummaryTab — P&L overview for a collection/event
 * Expandable sections with inline editing, artist/buyer search, fixed submission fee panel,
 * and split-order support for multi-print transactions.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../utils/supabaseClient';
import RichTextEditor from '../../ui/RichTextEditor';
import {
  ShoppingBag, Image, Printer, Ticket, FileText,
  RefreshCw, TrendingUp, AlertCircle, ChevronDown, ChevronUp,
  Edit, Check, X, ExternalLink, Scissors, DollarSign, Plus, Trash2,
} from 'lucide-react';

const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const fmtDate = (iso) => {
  if (!iso) return '\u2014';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// ── Reusable async search dropdown ──────────────────────────────────────────
const SearchField = ({ initialText = '', onSearch, onSelect, onClear, placeholder, optional }) => {
  const [text, setText] = useState(initialText);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (e) => {
    const q = e.target.value;
    setText(q);
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setSearching(true);
      const r = await onSearch(q);
      setResults(r || []);
      setOpen(true);
      setSearching(false);
    }, 280);
  };

  const handleSelect = (item) => {
    setText(item.label);
    setOpen(false);
    setResults([]);
    onSelect(item);
  };

  const handleClear = () => {
    setText('');
    setResults([]);
    setOpen(false);
    if (onClear) onClear();
  };

  const inp = { padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, background: 'white', color: '#1f2937', width: '100%', boxSizing: 'border-box' };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input
          style={{ ...inp, paddingRight: text ? 20 : 6 }}
          value={text}
          onChange={handleChange}
          placeholder={placeholder || (optional ? 'optional\u2026' : 'Search\u2026')}
        />
        {(text || searching) && (
          <span style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', cursor: text ? 'pointer' : 'default', color: '#9ca3af', lineHeight: 1, display: 'flex', alignItems: 'center' }}
            onMouseDown={text ? handleClear : undefined}>
            {searching ? <RefreshCw size={10} /> : <X size={10} />}
          </span>
        )}
      </div>
      {open && results.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 2, background: 'white', border: '1px solid #d1d5db', borderRadius: 6, zIndex: 200, maxHeight: 180, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}>
          {results.map((item) => (
            <div key={item.id} onMouseDown={() => handleSelect(item)}
              style={{ padding: '6px 10px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#1f2937' }}>{item.label}</div>
              {item.sublabel && <div style={{ fontSize: 11, color: '#6b7280' }}>{item.sublabel}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Search helpers ───────────────────────────────────────────────────────────
const searchArtists = async (q) => {
  const { data } = await supabase
    .from('users')
    .select('uuid, nickname, first_name, last_name, email')
    .or(`nickname.ilike.%${q}%,email.ilike.%${q}%,first_name.ilike.%${q}%,last_name.ilike.%${q}%`)
    .limit(10);
  return (data || []).map((u) => ({
    id: u.uuid,
    label: u.nickname || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email,
    sublabel: u.email,
    value: u,
  }));
};

const searchBuyers = async (q) => {
  const [{ data: users }, { data: prospects }] = await Promise.all([
    supabase.from('users').select('uuid, nickname, first_name, last_name, email')
      .or(`email.ilike.%${q}%,first_name.ilike.%${q}%,last_name.ilike.%${q}%,nickname.ilike.%${q}%`).limit(8),
    supabase.from('user_prospects').select('id, uuid, first_name, last_name, email')
      .or(`email.ilike.%${q}%,first_name.ilike.%${q}%,last_name.ilike.%${q}%`).limit(5),
  ]);
  return [
    ...(users || []).map((u) => ({
      id: `u-${u.uuid}`,
      label: u.nickname || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email,
      sublabel: `${u.email} \u00b7 user`,
      value: { ...u, _source: 'user' },
    })),
    ...(prospects || []).map((p) => ({
      id: `p-${p.id}`,
      label: [p.first_name, p.last_name].filter(Boolean).join(' ') || p.email,
      sublabel: `${p.email} \u00b7 prospect`,
      value: { ...p, _source: 'prospect' },
    })),
  ];
};

// ── Cash sale inline form ────────────────────────────────────────────────────
const CashSaleForm = ({ eventIds, productType, onSaved, onCancel }) => {
  const [form, setForm] = useState({ product_name: '', price: '', notes: 'cash sale' });
  const [saving, setSaving] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(eventIds?.[0] ?? null);
  const artistUuidRef = useRef(null);
  const buyerUuidRef = useRef(null);
  const buyerFieldsRef = useRef({ first_name: '', last_name: '', email: '' });

  const inp = { padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, background: 'white', color: '#1f2937', width: '100%', boxSizing: 'border-box' };

  const save = async () => {
    if (!form.product_name.trim() || !form.price) return;
    setSaving(true);
    const { error } = await supabase.from('orders').insert({
      event_id: selectedEventId,
      product_name: form.product_name.trim(),
      product_type: productType,
      price: parseFloat(form.price) || 0,
      fulfillment_status: 'paid',
      notes: form.notes || 'cash sale',
      artist_uuid: artistUuidRef.current || null,
      uuid: buyerUuidRef.current || null,
      first_name: buyerFieldsRef.current.first_name || null,
      last_name: buyerFieldsRef.current.last_name || null,
      email: buyerFieldsRef.current.email || null,
      purchased_at: new Date().toISOString(),
    });
    setSaving(false);
    if (error) { alert('Error saving: ' + error.message); } else { onSaved(); }
  };

  const canSave = form.product_name.trim() && form.price && !saving;

  return (
    <div style={{ padding: '14px 14px', background: '#f0fdf4', borderTop: '2px solid #bbf7d0' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#15803d', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        <DollarSign size={13} /> Add Cash Sale
      </div>
      {eventIds.length > 1 && (
        <div style={{ marginBottom: 8 }}>
          <select style={{ ...inp, maxWidth: 240 }} value={selectedEventId ?? ''} onChange={(e) => setSelectedEventId(Number(e.target.value))}>
            {eventIds.map((id) => <option key={id} value={id}>Event #{id}</option>)}
          </select>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px', gap: 6, marginBottom: 6 }}>
        <input style={inp} placeholder="Product name *" value={form.product_name}
          onChange={(e) => setForm((f) => ({ ...f, product_name: e.target.value }))} />
        <input style={{ ...inp, width: 86 }} type="number" step="0.01" min="0" placeholder="Price *" value={form.price}
          onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 2 }}>Artist (optional)</div>
          <SearchField
            key="cash-artist"
            onSearch={searchArtists}
            onSelect={(item) => { artistUuidRef.current = item.value.uuid; }}
            onClear={() => { artistUuidRef.current = null; }}
            optional placeholder="Search artist\u2026"
          />
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 2 }}>Buyer (optional)</div>
          <SearchField
            key="cash-buyer"
            onSearch={searchBuyers}
            onSelect={(item) => {
              const u = item.value;
              buyerFieldsRef.current = { first_name: u.first_name || '', last_name: u.last_name || '', email: u.email || '' };
              buyerUuidRef.current = u._source === 'user' ? u.uuid : null;
            }}
            onClear={() => { buyerFieldsRef.current = { first_name: '', last_name: '', email: '' }; buyerUuidRef.current = null; }}
            optional placeholder="Search buyer\u2026"
          />
        </div>
      </div>
      <div style={{ marginBottom: 8 }}>
        <input style={inp} placeholder="Notes (default: cash sale)" value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={save} disabled={!canSave}
          style={{ background: canSave ? '#16a34a' : '#d1fae5', color: canSave ? 'white' : '#6b7280', border: 'none', borderRadius: 4, padding: '5px 12px', cursor: canSave ? 'pointer' : 'not-allowed', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
          <Check size={12} /> {saving ? 'Saving\u2026' : 'Save Cash Sale'}
        </button>
        <button onClick={onCancel}
          style={{ background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 4, padding: '5px 12px', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
          <X size={12} /> Cancel
        </button>
      </div>
    </div>
  );
};

// ── Inline-editable order row (with split support) ──────────────────────────
const OrderRow = ({ order, artistLookup = {}, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [splitting, setSplitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [artistInitial, setArtistInitial] = useState('');
  const [buyerInitial, setBuyerInitial] = useState('');
  const [splitItems, setSplitItems] = useState([]);

  const artistUuidRef = useRef(order.artist_uuid || null);
  const buyerUuidRef = useRef(order.uuid || null);
  const buyerFieldsRef = useRef({ first_name: order.first_name || '', last_name: order.last_name || '', email: order.email || '' });

  // ── Edit mode ──
  const startEdit = () => {
    artistUuidRef.current = order.artist_uuid || null;
    buyerUuidRef.current = order.uuid || null;
    buyerFieldsRef.current = { first_name: order.first_name || '', last_name: order.last_name || '', email: order.email || '' };
    setForm({
      product_name: order.product_name || '',
      product_type: order.product_type || 'original',
      price: order.price?.toString() || '',
      fulfillment_status: order.fulfillment_status || 'paid',
      notes: order.notes || '',
    });
    setArtistInitial(order.artist_uuid ? (artistLookup[order.artist_uuid] || '') : '');
    setBuyerInitial([order.first_name, order.last_name].filter(Boolean).join(' ') || order.email || '');
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    const { first_name, last_name, email } = buyerFieldsRef.current;
    const { error } = await supabase.from('orders').update({
      product_name: form.product_name,
      product_type: form.product_type,
      price: parseFloat(form.price),
      fulfillment_status: form.fulfillment_status,
      notes: form.notes,
      artist_uuid: artistUuidRef.current || null,
      uuid: buyerUuidRef.current || null,
      first_name,
      last_name,
      email,
    }).eq('id', order.id);
    setSaving(false);
    if (!error) {
      setEditing(false);
      onSaved({ ...order, ...form, price: parseFloat(form.price), artist_uuid: artistUuidRef.current, first_name, last_name, email });
    } else {
      alert('Save failed: ' + error.message);
    }
  };

  // ── Split mode ──
  const startSplit = () => {
    setSplitItems([{
      _key: Date.now(),
      product_name: order.product_name || '',
      price: order.price?.toString() || '',
      artistUuid: order.artist_uuid || null,
      artistText: order.artist_uuid ? (artistLookup[order.artist_uuid] || '') : '',
    }]);
    setSplitting(true);
  };

  const addSplitItem = () => {
    setSplitItems((prev) => [...prev, {
      _key: Date.now() + Math.random(),
      product_name: '',
      price: '',
      artistUuid: order.artist_uuid || null,
      artistText: order.artist_uuid ? (artistLookup[order.artist_uuid] || '') : '',
    }]);
  };

  const removeSplitItem = (idx) => setSplitItems((prev) => prev.filter((_, i) => i !== idx));

  const updateSplitItem = (idx, field, value) =>
    setSplitItems((prev) => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));

  const saveSplit = async () => {
    if (splitItems.length === 0) return;
    setSaving(true);
    const [first, ...rest] = splitItems;

    const { error: updateErr } = await supabase.from('orders').update({
      product_name: first.product_name,
      price: parseFloat(first.price) || 0,
      artist_uuid: first.artistUuid || null,
    }).eq('id', order.id);

    if (updateErr) {
      alert('Error updating order: ' + updateErr.message);
      setSaving(false);
      return;
    }

    if (rest.length > 0) {
      const newRows = rest.map((item) => ({
        event_id: order.event_id,
        order_num: order.order_num,
        product_name: item.product_name,
        product_type: order.product_type,
        price: parseFloat(item.price) || 0,
        fulfillment_status: order.fulfillment_status,
        notes: order.notes || null,
        first_name: order.first_name || null,
        last_name: order.last_name || null,
        email: order.email || null,
        uuid: order.uuid || null,
        artist_uuid: item.artistUuid || null,
        purchased_at: order.purchased_at || null,
        stripe_payment_id: order.stripe_payment_id || null,
        sale_location: order.sale_location || null,
      }));
      const { error: insertErr } = await supabase.from('orders').insert(newRows);
      if (insertErr) {
        alert('Error creating split rows: ' + insertErr.message);
        setSaving(false);
        return;
      }
    }

    setSaving(false);
    setSplitting(false);
    // Trigger full reload since we inserted new rows
    onSaved({ ...order, product_name: first.product_name, price: parseFloat(first.price) || 0, artist_uuid: first.artistUuid, _reloadAll: true });
  };

  const inp = { padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, background: 'white', color: '#1f2937', width: '100%', boxSizing: 'border-box' };

  // ── Split form render ──
  if (splitting) {
    const splitTotal = splitItems.reduce((s, item) => s + (parseFloat(item.price) || 0), 0);
    const origTotal = parseFloat(order.price) || 0;
    const diff = Math.abs(splitTotal - origTotal);
    const balanced = diff < 0.01;

    return (
      <tr style={{ background: '#fffbeb' }}>
        <td colSpan={7} style={{ padding: '12px 10px' }}>
          <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#92400e', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Scissors size={14} /> Split into individual line items
            </span>
            <span style={{ fontSize: 12, color: balanced ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
              {fmt(splitTotal)} / {fmt(origTotal)}
              {!balanced && <span style={{ fontWeight: 400, marginLeft: 4 }}>(off by {fmt(diff)})</span>}
              {balanced && <span style={{ fontWeight: 400, marginLeft: 4 }}>\u2713 balanced</span>}
            </span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
            <thead>
              <tr style={{ background: '#fef3c7' }}>
                <th style={{ padding: '5px 6px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#92400e', textTransform: 'uppercase' }}>Product / Print Title</th>
                <th style={{ padding: '5px 6px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#92400e', textTransform: 'uppercase', width: 90 }}>Price</th>
                <th style={{ padding: '5px 6px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#92400e', textTransform: 'uppercase' }}>Artist</th>
                <th style={{ width: 30 }}></th>
              </tr>
            </thead>
            <tbody>
              {splitItems.map((item, idx) => (
                <tr key={item._key} style={{ borderBottom: '1px solid #fde68a' }}>
                  <td style={{ padding: '4px 6px' }}>
                    <input style={inp} value={item.product_name} placeholder="Print / artwork title\u2026"
                      onChange={(e) => updateSplitItem(idx, 'product_name', e.target.value)} />
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <input style={{ ...inp, width: 80 }} type="number" step="0.01" value={item.price} placeholder="0.00"
                      onChange={(e) => updateSplitItem(idx, 'price', e.target.value)} />
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <SearchField
                      key={`split-artist-${item._key}`}
                      initialText={item.artistText}
                      onSearch={searchArtists}
                      onSelect={(sel) => updateSplitItem(idx, 'artistUuid', sel.value.uuid)}
                      onClear={() => updateSplitItem(idx, 'artistUuid', null)}
                      optional
                      placeholder="Search artist\u2026"
                    />
                  </td>
                  <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                    {splitItems.length > 1 && (
                      <button onClick={() => removeSplitItem(idx)}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 2, lineHeight: 1 }}>
                        <X size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={addSplitItem}
              style={{ background: '#fef9c3', color: '#92400e', border: '1px solid #fde68a', borderRadius: 4, padding: '5px 10px', cursor: 'pointer', fontSize: 12 }}>
              + Add item
            </button>
            <button onClick={saveSplit} disabled={saving}
              style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: 4, padding: '5px 12px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Check size={12} /> {saving ? 'Saving\u2026' : !balanced ? 'Save (unbalanced)' : 'Save Split'}
            </button>
            <button onClick={() => setSplitting(false)}
              style={{ background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 4, padding: '5px 12px', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
              <X size={12} /> Cancel
            </button>
            {!balanced && (
              <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 4 }}>
                Prices don\u2019t need to balance \u2014 save when ready
              </span>
            )}
          </div>
        </td>
      </tr>
    );
  }

  // ── Edit form render ──
  if (editing) {
    return (
      <tr style={{ background: '#fafafa' }}>
        <td colSpan={7} style={{ padding: '10px 10px' }}>
          {/* Row 1: product fields */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 80px 120px', gap: 6, marginBottom: 8 }}>
            <input style={inp} value={form.product_name} placeholder="Product name"
              onChange={(e) => setForm((f) => ({ ...f, product_name: e.target.value }))} />
            <select style={inp} value={form.product_type}
              onChange={(e) => setForm((f) => ({ ...f, product_type: e.target.value }))}>
              <option value="original">Original</option>
              <option value="print">Print</option>
              <option value="merch">Merch</option>
            </select>
            <input style={inp} type="number" value={form.price} placeholder="Price"
              onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} />
            <select style={inp} value={form.fulfillment_status}
              onChange={(e) => setForm((f) => ({ ...f, fulfillment_status: e.target.value }))}>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="fulfilled">Fulfilled</option>
              <option value="shipped">Shipped</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          {/* Row 2: artist, buyer, notes */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>Artist</div>
              <SearchField
                key={`artist-${order.id}`}
                initialText={artistInitial}
                onSearch={searchArtists}
                onSelect={(item) => { artistUuidRef.current = item.value.uuid; }}
                onClear={() => { artistUuidRef.current = null; }}
                optional
                placeholder="Search artists\u2026"
              />
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>Buyer</div>
              <SearchField
                key={`buyer-${order.id}`}
                initialText={buyerInitial}
                onSearch={searchBuyers}
                onSelect={(item) => {
                  const u = item.value;
                  buyerFieldsRef.current = { first_name: u.first_name || '', last_name: u.last_name || '', email: u.email || '' };
                  buyerUuidRef.current = u._source === 'user' ? u.uuid : null;
                }}
                placeholder="Search buyer\u2026"
              />
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>Notes</div>
              <input style={inp} value={form.notes} placeholder="notes\u2026"
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={save} disabled={saving} style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: 4, padding: '5px 12px', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Check size={12} /> {saving ? 'Saving\u2026' : 'Save'}
            </button>
            <button onClick={() => setEditing(false)} style={{ background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 4, padding: '5px 12px', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
              <X size={12} /> Cancel
            </button>
          </div>
        </td>
      </tr>
    );
  }

  // ── Read-only row ──
  const artistName = order.artist_uuid ? (artistLookup[order.artist_uuid] || '\u2014') : '\u2014';
  const buyerName = [order.first_name, order.last_name].filter(Boolean).join(' ') || order.email || '\u2014';
  return (
    <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
      <td style={{ padding: '8px 6px', fontSize: 13, color: '#1f2937' }}>{order.product_name || '\u2014'}</td>
      <td style={{ padding: '8px 6px' }}>
        <span style={{
          fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 9999,
          background: order.product_type === 'original' ? '#ede9fe' : order.product_type === 'print' ? '#e0f2fe' : '#fef3c7',
          color: order.product_type === 'original' ? '#6d28d9' : order.product_type === 'print' ? '#0369a1' : '#92400e',
        }}>{order.product_type || '\u2014'}</span>
      </td>
      <td style={{ padding: '8px 6px', fontSize: 13, fontWeight: 600, color: '#1f2937' }}>{fmt(order.price)}</td>
      <td style={{ padding: '8px 6px', fontSize: 12, color: '#6b7280' }}>{order.fulfillment_status || '\u2014'}</td>
      <td style={{ padding: '8px 6px', fontSize: 12, color: '#374151' }}>{buyerName}</td>
      <td style={{ padding: '8px 6px', fontSize: 12, color: '#374151' }}>{artistName}</td>
      <td style={{ padding: '8px 6px' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={startEdit} title="Edit"
            style={{ background: 'transparent', border: '1px solid #e5e7eb', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>
            <Edit size={12} />
          </button>
          <button onClick={startSplit} title="Split into multiple line items"
            style={{ background: 'transparent', border: '1px solid #fde68a', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', color: '#92400e', lineHeight: 1 }}>
            <Scissors size={12} />
          </button>
        </div>
      </td>
    </tr>
  );
};

// ── Expandable section wrapper ───────────────────────────────────────────────
const Section = ({ icon: Icon, label, count, revenue, color, sublabel, expanded, onToggle, children }) => (
  <div style={{ marginBottom: 8 }}>
    <button onClick={onToggle} style={{
      width: '100%', display: 'flex', alignItems: 'center', gap: 14,
      padding: '14px 18px', background: expanded ? '#f9fafb' : 'white',
      borderRadius: expanded ? '10px 10px 0 0' : 10,
      border: `1px solid ${expanded ? '#d1d5db' : '#e5e7eb'}`,
      borderBottom: expanded ? 'none' : undefined,
      cursor: 'pointer', textAlign: 'left',
    }}>
      <div style={{ width: 36, height: 36, borderRadius: 8, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={18} color={color} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: '#1f2937' }}>{label}</div>
        {sublabel && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{sublabel}</div>}
      </div>
      <div style={{ textAlign: 'right', marginRight: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: '#1f2937' }}>{fmt(revenue)}</div>
        {count !== null && <div style={{ fontSize: 12, color: '#9ca3af' }}>{count} {count === 1 ? 'sale' : 'sales'}</div>}
      </div>
      {expanded ? <ChevronUp size={16} color="#9ca3af" /> : <ChevronDown size={16} color="#9ca3af" />}
    </button>
    {expanded && (
      <div style={{ border: '1px solid #d1d5db', borderTop: 'none', borderRadius: '0 0 10px 10px', background: 'white', overflow: 'hidden' }}>
        {children}
      </div>
    )}
  </div>
);

// ── Orders detail (originals / prints) ──────────────────────────────────────
const TH = ({ children }) => (
  <th style={{ padding: '8px 6px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>{children}</th>
);

const OrdersDetail = ({ eventIds, productType, onDataChange }) => {
  const [rows, setRows] = useState(null);
  const [artistLookup, setArtistLookup] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCashForm, setShowCashForm] = useState(false);

  const fetchRows = useCallback(() => {
    if (!eventIds?.length) { setLoading(false); return; }
    setLoading(true);
    supabase
      .from('orders')
      .select('id, event_id, order_num, product_name, product_type, price, fulfillment_status, notes, first_name, last_name, email, uuid, artist_uuid, purchased_at, stripe_payment_id, sale_location')
      .in('event_id', eventIds).eq('product_type', productType).neq('fulfillment_status', 'cancelled')
      .order('purchased_at', { ascending: false })
      .then(async ({ data, error }) => {
        const fetched = error ? [] : (data || []);
        setRows(fetched);
        const uuids = [...new Set(fetched.map((r) => r.artist_uuid).filter(Boolean))];
        if (uuids.length > 0) {
          const { data: artists } = await supabase.from('users').select('uuid, nickname, first_name, last_name').in('uuid', uuids);
          const map = {};
          for (const a of artists || []) map[a.uuid] = a.nickname || [a.first_name, a.last_name].filter(Boolean).join(' ');
          setArtistLookup(map);
        }
        setLoading(false);
      });
  }, [eventIds, productType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleSaved = (updated) => {
    if (updated._reloadAll) {
      fetchRows();
      if (onDataChange) onDataChange();
    } else {
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      if (onDataChange) onDataChange();
    }
  };

  const cashFormEl = showCashForm
    ? <CashSaleForm eventIds={eventIds} productType={productType}
        onSaved={() => { setShowCashForm(false); fetchRows(); if (onDataChange) onDataChange(); }}
        onCancel={() => setShowCashForm(false)} />
    : null;

  const addCashBtn = (
    <button onClick={() => setShowCashForm(true)}
      style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 500 }}>
      <DollarSign size={11} /> Cash Sale
    </button>
  );

  if (loading) return <div style={{ padding: 16, fontSize: 13, color: '#6b7280' }}>Loading\u2026</div>;

  if (!rows?.length) {
    return (
      <div>
        <div style={{ padding: 16, fontSize: 13, color: '#9ca3af' }}>No orders found.</div>
        {cashFormEl || (
          <div style={{ padding: '8px 12px', borderTop: '1px solid #f3f4f6' }}>{addCashBtn}</div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
              <TH>Product</TH><TH>Type</TH><TH>Price</TH><TH>Status</TH><TH>Buyer</TH><TH>Artist</TH>
              <th style={{ padding: '8px 6px', width: 64 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((order) => <OrderRow key={order.id} order={order} artistLookup={artistLookup} onSaved={handleSaved} />)}
          </tbody>
        </table>
      </div>
      {cashFormEl || (
        <div style={{ padding: '8px 12px', fontSize: 12, color: '#9ca3af', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{rows.length} orders \u00b7 {fmt(rows.reduce((s, r) => s + parseFloat(r.price || 0), 0))} total</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {addCashBtn}
            <span style={{ fontSize: 11, color: '#d1d5db' }}>|</span>
            <span style={{ fontSize: 11, color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Scissors size={10} /> = split multi-item order
            </span>
            <a href="/admin/orders" target="_blank" rel="noopener noreferrer"
              style={{ color: '#6366f1', display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none', fontSize: 12 }}>
              Open in Orders Admin <ExternalLink size={11} />
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Merch detail ─────────────────────────────────────────────────────────────
const MerchDetail = ({ eventIds, onDataChange }) => {
  const [orderRows, setOrderRows] = useState(null);
  const [preOrders, setPreOrders] = useState(null);
  const [artistLookup, setArtistLookup] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCashForm, setShowCashForm] = useState(false);

  const fetchRows = useCallback(() => {
    if (!eventIds?.length) { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      supabase.from('orders')
        .select('id, event_id, order_num, product_name, product_type, price, fulfillment_status, notes, first_name, last_name, email, uuid, artist_uuid, purchased_at, stripe_payment_id, sale_location')
        .in('event_id', eventIds).eq('product_type', 'merch').neq('fulfillment_status', 'cancelled')
        .order('purchased_at', { ascending: false }),
      supabase.from('event_merch_orders')
        .select('id, merch_id, email, first_name, last_name, size, quantity, unit_price, total_amount, payment_status, fulfillment_status, created_at')
        .in('event_id', eventIds).eq('payment_status', 'paid').order('created_at', { ascending: false }),
    ]).then(async ([ordRes, preRes]) => {
      const fetched = ordRes.data || [];
      setOrderRows(fetched);
      setPreOrders(preRes.data || []);
      const uuids = [...new Set(fetched.map((r) => r.artist_uuid).filter(Boolean))];
      if (uuids.length > 0) {
        const { data: artists } = await supabase.from('users').select('uuid, nickname, first_name, last_name').in('uuid', uuids);
        const map = {};
        for (const a of artists || []) map[a.uuid] = a.nickname || [a.first_name, a.last_name].filter(Boolean).join(' ');
        setArtistLookup(map);
      }
      setLoading(false);
    });
  }, [eventIds]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleSaved = (updated) => {
    if (updated._reloadAll) {
      fetchRows();
      if (onDataChange) onDataChange();
    } else {
      setOrderRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      if (onDataChange) onDataChange();
    }
  };

  if (loading) return <div style={{ padding: 16, fontSize: 13, color: '#6b7280' }}>Loading\u2026</div>;

  return (
    <div>
      {orderRows?.length > 0 && (
        <>
          <div style={{ padding: '8px 12px', fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
            Terminal / Manual Orders ({orderRows.length})
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                  <TH>Product</TH><TH>Type</TH><TH>Price</TH><TH>Status</TH><TH>Buyer</TH><TH>Artist</TH>
                  <th style={{ padding: '8px 6px', width: 64 }}></th>
                </tr>
              </thead>
              <tbody>
                {orderRows.map((order) => <OrderRow key={order.id} order={order} artistLookup={artistLookup} onSaved={handleSaved} />)}
              </tbody>
            </table>
          </div>
        </>
      )}
      {preOrders?.length > 0 && (
        <>
          <div style={{ padding: '8px 12px', fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', background: '#f8fafc', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
            Event Pre-Orders ({preOrders.length})
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                  <TH>Buyer</TH><TH>Size</TH><TH>Qty</TH><TH>Total</TH><TH>Fulfillment</TH><TH>Date</TH>
                </tr>
              </thead>
              <tbody>
                {preOrders.map((po) => (
                  <tr key={po.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '8px 6px', fontSize: 13 }}>{[po.first_name, po.last_name].filter(Boolean).join(' ') || po.email || '\u2014'}</td>
                    <td style={{ padding: '8px 6px', fontSize: 13 }}>{po.size || '\u2014'}</td>
                    <td style={{ padding: '8px 6px', fontSize: 13 }}>{po.quantity}</td>
                    <td style={{ padding: '8px 6px', fontSize: 13, fontWeight: 600 }}>{fmt(po.total_amount)}</td>
                    <td style={{ padding: '8px 6px', fontSize: 12, color: '#6b7280' }}>{po.fulfillment_status || '\u2014'}</td>
                    <td style={{ padding: '8px 6px', fontSize: 12, color: '#9ca3af' }}>{fmtDate(po.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {!orderRows?.length && !preOrders?.length && (
        <div style={{ padding: 16, fontSize: 13, color: '#9ca3af' }}>No merch orders found.</div>
      )}
      {showCashForm
        ? <CashSaleForm eventIds={eventIds} productType="merch"
            onSaved={() => { setShowCashForm(false); fetchRows(); if (onDataChange) onDataChange(); }}
            onCancel={() => setShowCashForm(false)} />
        : (
          <div style={{ padding: '8px 12px', fontSize: 12, color: '#9ca3af', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{(orderRows?.length || 0) + (preOrders?.length || 0)} total orders</span>
            <button onClick={() => setShowCashForm(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 500 }}>
              <DollarSign size={11} /> Cash Sale
            </button>
          </div>
        )
      }
    </div>
  );
};

// ── Tickets detail ───────────────────────────────────────────────────────────
const TicketsDetail = ({ collectionEvents, eventIds, onDataChange }) => {
  const [showCashForm, setShowCashForm] = useState(false);
  const [cashForm, setCashForm] = useState({ event_id: eventIds?.[0] ?? null, amount: '', buyer_name: '', buyer_email: '', notes: 'cash ticket' });
  const [saving, setSaving] = useState(false);

  const inp = { padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, background: 'white', color: '#1f2937', width: '100%', boxSizing: 'border-box' };

  const saveTicket = async () => {
    if (!cashForm.amount) return;
    setSaving(true);
    const nameParts = cashForm.buyer_name.trim().split(/\s+/);
    const { error } = await supabase.from('orders').insert({
      event_id: cashForm.event_id,
      product_name: 'Ticket (cash)',
      product_type: 'ticket',
      price: parseFloat(cashForm.amount) || 0,
      fulfillment_status: 'paid',
      notes: cashForm.notes || 'cash ticket',
      first_name: nameParts[0] || null,
      last_name: nameParts.slice(1).join(' ') || null,
      email: cashForm.buyer_email || null,
      purchased_at: new Date().toISOString(),
    });
    setSaving(false);
    if (error) { alert('Error: ' + error.message); }
    else { setShowCashForm(false); setCashForm((f) => ({ ...f, amount: '', buyer_name: '', buyer_email: '' })); if (onDataChange) onDataChange(); }
  };

  return (
    <div>
      {collectionEvents.map((ev) => (
        <div key={ev.id} style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>{ev.name || ev.title || `Event #${ev.id}`}</div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
              {ev.tickets_sold} tickets sold{ev.galleries?.name ? ` \u00b7 ${ev.galleries.name}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: '#1f2937' }}>{fmt(ev.total_revenue)}</span>
            <a href={`/admin/events/${ev.id}`} target="_blank" rel="noopener noreferrer" title="Open event detail"
              style={{ color: '#6366f1', display: 'flex', alignItems: 'center' }}>
              <ExternalLink size={14} />
            </a>
          </div>
        </div>
      ))}
      {showCashForm ? (
        <div style={{ padding: '14px 16px', background: '#f0fdf4', borderTop: '2px solid #bbf7d0' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#15803d', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <DollarSign size={13} /> Add Cash Ticket
          </div>
          {eventIds.length > 1 && (
            <div style={{ marginBottom: 8 }}>
              <select style={{ ...inp, maxWidth: 240 }} value={cashForm.event_id ?? ''} onChange={(e) => setCashForm((f) => ({ ...f, event_id: Number(e.target.value) }))}>
                {collectionEvents.map((ev) => <option key={ev.id} value={ev.id}>{ev.name || ev.title || `Event #${ev.id}`}</option>)}
              </select>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 1fr', gap: 6, marginBottom: 6 }}>
            <input style={{ ...inp, width: 86 }} type="number" step="0.01" min="0" placeholder="Amount *" value={cashForm.amount}
              onChange={(e) => setCashForm((f) => ({ ...f, amount: e.target.value }))} />
            <input style={inp} placeholder="Buyer name" value={cashForm.buyer_name}
              onChange={(e) => setCashForm((f) => ({ ...f, buyer_name: e.target.value }))} />
            <input style={inp} type="email" placeholder="Buyer email" value={cashForm.buyer_email}
              onChange={(e) => setCashForm((f) => ({ ...f, buyer_email: e.target.value }))} />
          </div>
          <div style={{ marginBottom: 8 }}>
            <input style={inp} placeholder="Notes (default: cash ticket)" value={cashForm.notes}
              onChange={(e) => setCashForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={saveTicket} disabled={saving || !cashForm.amount}
              style={{ background: saving || !cashForm.amount ? '#d1fae5' : '#16a34a', color: saving || !cashForm.amount ? '#6b7280' : 'white', border: 'none', borderRadius: 4, padding: '5px 12px', cursor: saving || !cashForm.amount ? 'not-allowed' : 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Check size={12} /> {saving ? 'Saving\u2026' : 'Save Cash Ticket'}
            </button>
            <button onClick={() => setShowCashForm(false)}
              style={{ background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 4, padding: '5px 12px', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
              <X size={12} /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <div style={{ padding: '8px 12px', borderTop: '1px solid #f3f4f6' }}>
          <button onClick={() => setShowCashForm(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 500 }}>
            <DollarSign size={11} /> Add Cash Ticket
          </button>
        </div>
      )}
    </div>
  );
};

// ── Submissions detail (fixed: uses paid column, two-step user lookup) ───────
const SubmissionsDetail = ({ collectionId, submissionDetails, onDataChange }) => {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [markingPaid, setMarkingPaid] = useState(null);
  const feeAmt = parseFloat(submissionDetails?.submission_price || 0);

  useEffect(() => {
    const fetchData = async () => {
      const { data: subs } = await supabase
        .from('submissions_details')
        .select('submission_id')
        .eq('collection_id', collectionId);
      if (!subs?.length) { setRows([]); setLoading(false); return; }
      const ids = subs.map((s) => s.submission_id).filter(Boolean);

      const { data: mappings } = await supabase
        .from('artist_submission_mapping')
        .select('id, submission_id, user_id, accepted_at, submitted_at, paid, accepted')
        .in('submission_id', ids)
        .order('accepted_at', { ascending: false });
      if (!mappings?.length) { setRows([]); setLoading(false); return; }

      const userUuids = [...new Set(mappings.map((m) => m.user_id).filter(Boolean))];
      const { data: users } = await supabase
        .from('users')
        .select('uuid, nickname, first_name, last_name, email')
        .in('uuid', userUuids);
      const userMap = {};
      for (const u of users || []) userMap[u.uuid] = u;
      setRows(mappings.map((m) => ({ ...m, user: userMap[m.user_id] || null })));
      setLoading(false);
    };
    fetchData();
  }, [collectionId]);

  const markPaid = async (rowId) => {
    setMarkingPaid(rowId);
    const { error } = await supabase.from('artist_submission_mapping').update({ paid: true }).eq('id', rowId);
    setMarkingPaid(null);
    if (error) { alert('Error: ' + error.message); return; }
    setRows((prev) => prev.map((r) => r.id === rowId ? { ...r, paid: true } : r));
    if (onDataChange) onDataChange();
  };

  if (loading) return <div style={{ padding: 16, fontSize: 13, color: '#6b7280' }}>Loading\u2026</div>;
  if (!rows?.length) return <div style={{ padding: 16, fontSize: 13, color: '#9ca3af' }}>No submissions found.</div>;

  const paidRows = rows.filter((r) => r.paid);
  const unpaidRows = rows.filter((r) => !r.paid);

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
            <TH>Artist</TH><TH>Email</TH><TH>Fee</TH><TH>Submitted</TH><TH>Accepted</TH><TH></TH>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const name = row.user?.nickname || [row.user?.first_name, row.user?.last_name].filter(Boolean).join(' ') || '\u2014';
            return (
              <tr key={row.id} style={{ borderBottom: '1px solid #f3f4f6', opacity: row.paid ? 1 : 0.6 }}>
                <td style={{ padding: '8px 6px', fontWeight: 500 }}>{name}</td>
                <td style={{ padding: '8px 6px', color: '#6b7280' }}>{row.user?.email || '\u2014'}</td>
                <td style={{ padding: '8px 6px', fontWeight: 600 }}>
                  {row.paid
                    ? fmt(feeAmt)
                    : <span style={{ fontSize: 11, color: '#9ca3af', background: '#f3f4f6', padding: '2px 7px', borderRadius: 9999 }}>Unpaid</span>}
                </td>
                <td style={{ padding: '8px 6px', color: '#9ca3af' }}>{fmtDate(row.submitted_at || row.accepted_at)}</td>
                <td style={{ padding: '8px 6px' }}>
                  {row.accepted === true
                    ? <span style={{ fontSize: 11, color: '#166534', background: '#dcfce7', padding: '2px 7px', borderRadius: 9999 }}>Accepted</span>
                    : row.accepted === false
                      ? <span style={{ fontSize: 11, color: '#92400e', background: '#fef3c7', padding: '2px 7px', borderRadius: 9999 }}>Not accepted</span>
                      : <span style={{ fontSize: 11, color: '#6b7280' }}>Pending</span>}
                </td>
                <td style={{ padding: '8px 6px' }}>
                  {!row.paid && (
                    <button onClick={() => markPaid(row.id)} disabled={markingPaid === row.id}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 5, padding: '3px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap' }}>
                      <DollarSign size={10} /> {markingPaid === row.id ? 'Saving\u2026' : 'Mark Paid'}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ padding: '8px 12px', fontSize: 12, color: '#9ca3af', borderTop: '1px solid #f3f4f6' }}>
        {paidRows.length} paid \u00b7 {unpaidRows.length} unpaid \u00b7 {fmt(paidRows.length * feeAmt)} collected
      </div>
    </div>
  );
};

// ── Artist Payouts Section ──────────────────────────────────────────────────
const COST_CATEGORIES = ['talent', 'production', 'marketing', 'labor', 'other'];

const PAYOUT_STATUS_STYLES = {
  pending:    { background: '#f3f4f6', color: '#6b7280', label: 'Pending' },
  emailed:    { background: '#eff6ff', color: '#1d4ed8', label: 'Emailed' },
  qbo_billed: { background: '#fef3c7', color: '#92400e', label: 'QBO Billed' },
  paid:       { background: '#dcfce7', color: '#166534', label: 'Paid' },
  cancelled:  { background: '#fef2f2', color: '#dc2626', label: 'Cancelled' },
};

const ArtistPayoutsSection = ({ collectionId, eventIds, onTotalChange }) => {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const [marking, setMarking] = useState({});
  const [payoutPreview, setPayoutPreview] = useState(null); // { idx, subject, htmlBody, recipientName, recipientEmail }
  const [collectionInfo, setCollectionInfo] = useState(null); // { title, organization_id, organizations }

  useEffect(() => {
    if (!collectionId) return;
    supabase
      .from('collections')
      .select('title, organization_id, organizations(name, contact_email)')
      .eq('collection_id', collectionId)
      .single()
      .then(({ data }) => { if (data) setCollectionInfo(data); });
  }, [collectionId]);

  const load = useCallback(async () => {
    if (!eventIds?.length) { setRows([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data: orders } = await supabase
        .from('orders')
        .select('artist_uuid, price, product_name, product_type')
        .in('event_id', eventIds)
        .in('product_type', ['original', 'print'])
        .neq('fulfillment_status', 'cancelled');

      const salesByArtist = {};
      const lineItemsByArtist = {};
      for (const o of orders || []) {
        if (!o.artist_uuid) continue;
        salesByArtist[o.artist_uuid] = (salesByArtist[o.artist_uuid] || 0) + parseFloat(o.price || 0);
        if (!lineItemsByArtist[o.artist_uuid]) lineItemsByArtist[o.artist_uuid] = [];
        lineItemsByArtist[o.artist_uuid].push({ name: o.product_name || 'Artwork', type: o.product_type, price: parseFloat(o.price || 0) });
      }
      const artistUuids = Object.keys(salesByArtist);
      if (artistUuids.length === 0) { setRows([]); setLoading(false); return; }

      const [{ data: users }, { data: rates }, { data: payoutRecords }] = await Promise.all([
        supabase.from('users').select('uuid, nickname, first_name, last_name, email, mobile_phone').in('uuid', artistUuids),
        supabase.from('collection_artist_rates').select('artist_uuid, commission_rate').eq('collection_id', collectionId),
        supabase.from('artist_payout_records').select('id, artist_uuid, amount, status, email_sent_at, email_opened_at, payment_confirmed_at, email_log_id').eq('collection_id', collectionId),
      ]);

      const nameMap = {};
      const emailMap = {};
      const mobileMap = {};
      for (const u of users || []) {
        nameMap[u.uuid] = u.nickname || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.uuid;
        emailMap[u.uuid] = u.email || null;
        mobileMap[u.uuid] = u.mobile_phone || null;
      }
      const rateMap = {};
      for (const r of rates || []) rateMap[r.artist_uuid] = parseFloat(r.commission_rate);
      const payoutMap = {};
      for (const p of payoutRecords || []) payoutMap[p.artist_uuid] = p;

      // Fetch email open data for any linked email_log rows
      const emailLogIds = (payoutRecords || []).map((p) => p.email_log_id).filter(Boolean);
      let emailOpenMap = {};
      if (emailLogIds.length > 0) {
        const { data: emailLogs } = await supabase
          .from('email_logs').select('id, opened_at, open_count').in('id', emailLogIds);
        for (const log of emailLogs || []) emailOpenMap[log.id] = log;
      }

      setRows(artistUuids.map((uuid) => {
        const pr = payoutMap[uuid] || null;
        const emailLog = pr?.email_log_id ? (emailOpenMap[pr.email_log_id] || null) : null;
        return {
          artistUuid: uuid,
          name: nameMap[uuid] || uuid,
          email: emailMap[uuid] || null,
          mobile_phone: mobileMap[uuid] || null,
          salesTotal: salesByArtist[uuid],
          lineItems: lineItemsByArtist[uuid] || [],
          rate: rateMap[uuid] ?? 0.35,
          rateInput: ((rateMap[uuid] ?? 0.35) * 100).toFixed(0),
          editing: false,
          saving: false,
          payoutRecordId: pr?.id || null,
          payoutStatus: pr?.status || 'pending',
          emailSentAt: pr?.email_sent_at || null,
          emailOpenedAt: pr?.email_opened_at || emailLog?.opened_at || null,
          paymentConfirmedAt: pr?.payment_confirmed_at || null,
          emailLogId: pr?.email_log_id || null,
        };
      }));
    } finally {
      setLoading(false);
    }
  }, [collectionId, eventIds]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!rows) return;
    const total = rows.reduce((s, r) => s + r.salesTotal * (1 - r.rate), 0);
    if (onTotalChange) onTotalChange(total);
  }, [rows, onTotalChange]);

  const saveRate = async (idx) => {
    const row = rows[idx];
    const newRate = Math.min(1, Math.max(0, parseFloat(row.rateInput) / 100));
    if (isNaN(newRate)) return;
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, saving: true } : r));
    const { error } = await supabase
      .from('collection_artist_rates')
      .upsert({ collection_id: collectionId, artist_uuid: row.artistUuid, commission_rate: newRate }, { onConflict: 'collection_id,artist_uuid' });
    setRows((prev) => prev.map((r, i) =>
      i === idx ? { ...r, rate: error ? r.rate : newRate, rateInput: ((error ? r.rate : newRate) * 100).toFixed(0), editing: false, saving: false } : r
    ));
    if (error) alert('Save failed: ' + error.message);
  };

  // Table version used in the actual sent email (email clients support tables)
  const buildLineItemsHtml = (lineItems) => {
    if (!lineItems?.length) return '';
    const itemRows = lineItems.map(li =>
      `<tr><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;">${li.name}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;color:#6b7280;text-transform:capitalize;">${li.type || ''}</td><td align="right" style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;font-weight:600;">${fmt(li.price)}</td></tr>`
    ).join('');
    return `<table width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;margin:12px 0;font-size:14px;"><thead><tr style="background:#f9fafb;"><th align="left" style="text-align:left;padding:6px 8px;border-bottom:2px solid #e5e7eb;font-weight:600;">Artwork</th><th align="left" style="text-align:left;padding:6px 8px;border-bottom:2px solid #e5e7eb;font-weight:600;">Type</th><th align="right" style="text-align:right;padding:6px 8px;border-bottom:2px solid #e5e7eb;font-weight:600;">Price</th></tr></thead><tbody>${itemRows}</tbody></table>`;
  };

  // List version used in the editor preview (Quill doesn't support tables)
  const buildLineItemsList = (lineItems) => {
    if (!lineItems?.length) return '';
    const items = lineItems.map(li =>
      `<li><strong>${li.name}</strong> <span style="color:#6b7280;text-transform:capitalize;">(${li.type || 'artwork'})</span> — ${fmt(li.price)}</li>`
    ).join('');
    return `<ul style="margin:8px 0;padding-left:20px;">${items}</ul>`;
  };

  const previewPayoutEmail = async (idx) => {
    const row = rows[idx];
    if (!row.email) { alert('No email address on file for this artist.'); return; }
    const payout = row.salesTotal * (1 - row.rate);
    const firstName = row.name.split(' ')[0] || row.name;
    const lineItemsHtml = buildLineItemsHtml(row.lineItems);   // table — for actual email
    const lineItemsList = buildLineItemsList(row.lineItems);   // list — for editor preview

    const applyVars = (str, listVersion) => str
      .replace(/\{first_name\}/g, firstName)
      .replace(/\{artist_name\}/g, row.name)
      .replace(/\{collection_name\}/g, collectionInfo?.title || 'Exhibition')
      .replace(/\{payout_amount\}/g, fmt(payout))
      .replace(/\{sales_total\}/g, fmt(row.salesTotal))
      .replace(/\{commission_pct\}/g, (row.rate * 100).toFixed(0))
      .replace(/\{payout_line_items\}/g, listVersion ? (lineItemsList || '') : (lineItemsHtml || ''))
      .replace(/\{contact_email\}/g, collectionInfo?.organizations?.contact_email || '')
      .replace(/\{organization_name\}/g, collectionInfo?.organizations?.name || 'The Gallery')
      .replace(/<p>(\s*<table[\s\S]*?<\/table>\s*)<\/p>/gi, '$1');

    // Try to load payout_notification template from DB for this collection's org
    if (collectionInfo?.organization_id) {
      const { data: tpl } = await supabase
        .from('organization_email_templates')
        .select('subject, html_body')
        .eq('organization_id', collectionInfo.organization_id)
        .eq('template_key', 'payout_notification')
        .maybeSingle();
      if (tpl) {
        const subject = tpl.subject
          .replace(/\{collection_name\}/g, collectionInfo.title || 'Exhibition')
          .replace(/\{payout_amount\}/g, fmt(payout));
        const htmlBody = applyVars(tpl.html_body, true);      // list version for editor
        const sendHtmlBody = applyVars(tpl.html_body, false); // table version for send
        setPayoutPreview({ idx, subject, htmlBody, sendHtmlBody, recipientName: row.name, recipientEmail: row.email, payout });
        return;
      }
    }

    // Fallback to hardcoded default
    const subject = `Your artwork payout: ${fmt(payout)}`;
    const htmlBody = `<p>Hi ${firstName},</p>
<p>Your artwork payout for this exhibition is <strong>${fmt(payout)}</strong>.</p>${lineItemsList ? `
<p><strong>Sales Breakdown:</strong></p>
${lineItemsList}` : ''}
<p>This reflects total artwork sales of <strong>${fmt(row.salesTotal)}</strong> minus the venue commission of <strong>${(row.rate * 100).toFixed(0)}%</strong>.</p>
<p>Payment will be processed shortly. You do not need to take any action at this time.</p>
<p>Thank you for participating!</p>`;
    const sendHtmlBody = `<p>Hi ${firstName},</p>
<p>Your artwork payout for this exhibition is <strong>${fmt(payout)}</strong>.</p>${lineItemsHtml ? `
<p><strong>Sales Breakdown:</strong></p>
${lineItemsHtml}` : ''}
<p>This reflects total artwork sales of <strong>${fmt(row.salesTotal)}</strong> minus the venue commission of <strong>${(row.rate * 100).toFixed(0)}%</strong>.</p>
<p>Payment will be processed shortly. You do not need to take any action at this time.</p>
<p>Thank you for participating!</p>`;
    setPayoutPreview({ idx, subject, htmlBody, sendHtmlBody, recipientName: row.name, recipientEmail: row.email, payout });
  };

  // Quick-fill templates for the one-off email modal
  const payoutEmailTemplates = [
    {
      label: 'Payout Notice',
      buildSubject: (row, payout) => `Your artwork payout: ${fmt(payout)}`,
      buildBody: (row, payout) => {
        const firstName = row.name.split(' ')[0] || row.name;
        const list = buildLineItemsList(row.lineItems);
        return `<p>Hi ${firstName},</p>\n<p>Your artwork payout for this exhibition is <strong>${fmt(payout)}</strong>.</p>${list ? `\n<p><strong>Sales Breakdown:</strong></p>\n${list}` : ''}\n<p>This reflects total artwork sales of <strong>${fmt(row.salesTotal)}</strong> minus the venue commission of <strong>${(row.rate * 100).toFixed(0)}%</strong>.</p>\n<p>Payment will be processed shortly. You do not need to take any action at this time.</p>\n<p>Thank you for participating!</p>`;
      },
      buildSendBody: (row, payout) => {
        const firstName = row.name.split(' ')[0] || row.name;
        const table = buildLineItemsHtml(row.lineItems);
        return `<p>Hi ${firstName},</p>\n<p>Your artwork payout for this exhibition is <strong>${fmt(payout)}</strong>.</p>${table ? `\n<p><strong>Sales Breakdown:</strong></p>\n${table}` : ''}\n<p>This reflects total artwork sales of <strong>${fmt(row.salesTotal)}</strong> minus the venue commission of <strong>${(row.rate * 100).toFixed(0)}%</strong>.</p>\n<p>Payment will be processed shortly. You do not need to take any action at this time.</p>\n<p>Thank you for participating!</p>`;
      },
    },
    {
      label: 'Payment Sent',
      buildSubject: (row, payout) => `Your payment of ${fmt(payout)} has been sent`,
      buildBody: (row, payout) => {
        const firstName = row.name.split(' ')[0] || row.name;
        return `<p>Hi ${firstName},</p>\n<p>Great news — your artwork payout of <strong>${fmt(payout)}</strong> has been sent!</p>\n<p>Please allow 3–5 business days for the funds to appear in your account.</p>\n<p>If you have any questions, please reply to this email.</p>\n<p>Thank you for your participation!</p>`;
      },
    },
    {
      label: 'Blank',
      buildSubject: () => '',
      buildBody: () => '',
    },
  ];

  const confirmSendPayoutEmail = async () => {
    if (!payoutPreview) return;
    const { idx, subject, htmlBody, sendHtmlBody } = payoutPreview;
    const emailHtml = sendHtmlBody || htmlBody; // use table version for actual send
    const row = rows[idx];
    setPayoutPreview(null);
    setSending((prev) => ({ ...prev, [row.artistUuid]: true }));
    try {
      const { data: upserted, error: upsertErr } = await supabase
        .from('artist_payout_records')
        .upsert({
          collection_id: collectionId,
          artist_uuid: row.artistUuid,
          amount: parseFloat((row.salesTotal * (1 - row.rate)).toFixed(2)),
          status: 'emailed',
          email_sent_at: new Date().toISOString(),
        }, { onConflict: 'collection_id,artist_uuid' })
        .select('id')
        .single();
      if (upsertErr) throw upsertErr;

      const { error: emailErr } = await supabase.functions.invoke('send-mandrill-email', {
        body: {
          subject,
          message: {
            to: [{ email: row.email, name: row.name }],
            html: emailHtml,
          },
        },
      });
      if (emailErr) throw emailErr;

      // Brief wait for edge function to write email_logs row, then link it
      await new Promise((res) => setTimeout(res, 900));
      const { data: logRows } = await supabase
        .from('email_logs').select('id')
        .eq('user_email', row.email)
        .eq('template_name', 'admin-notification')
        .order('sent_at', { ascending: false })
        .limit(1);
      const logId = logRows?.[0]?.id || null;
      if (logId) {
        await supabase.from('artist_payout_records').update({ email_log_id: logId }).eq('id', upserted.id);
      }
      setRows((prev) => prev.map((r, i) => i === idx ? {
        ...r, payoutRecordId: upserted.id, payoutStatus: 'emailed',
        emailSentAt: new Date().toISOString(), emailLogId: logId,
      } : r));
    } catch (err) {
      alert('Failed to send payout email: ' + err.message);
    } finally {
      setSending((prev) => ({ ...prev, [row.artistUuid]: false }));
    }
  };

  const markPaid = async (idx) => {
    const row = rows[idx];
    setMarking((prev) => ({ ...prev, [row.artistUuid]: true }));
    try {
      const payout = row.salesTotal * (1 - row.rate);
      const confirmedAt = new Date().toISOString();
      const { error } = await supabase
        .from('artist_payout_records')
        .upsert({
          collection_id: collectionId,
          artist_uuid: row.artistUuid,
          amount: parseFloat(payout.toFixed(2)),
          status: 'paid',
          payment_confirmed_at: confirmedAt,
        }, { onConflict: 'collection_id,artist_uuid' });
      if (error) throw error;
      setRows((prev) => prev.map((r, i) => i === idx ? {
        ...r, payoutStatus: 'paid', paymentConfirmedAt: confirmedAt,
      } : r));
    } catch (err) {
      alert('Failed to mark as paid: ' + err.message);
    } finally {
      setMarking((prev) => ({ ...prev, [row.artistUuid]: false }));
    }
  };

  const exportPayoutsCSV = () => {
    // QBO Vendor Import format (matches QuickBooks Online vendor import template)
    const today = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    const isoDate = new Date().toISOString().slice(0, 10);
    const header = ['Name', 'Company', 'Email', 'Phone', 'Mobile', 'Fax', 'Website', 'Street', 'City', 'State ', 'ZIP', 'Country', 'Opening Balance', 'Date', 'Tax ID'];
    const esc = (v) => `"${String(v || '').replace(/"/g, '""')}"`;
    const csvRows = rows
      .filter((r) => r.payoutStatus !== 'cancelled')
      .map((r) => {
        const payout = (r.salesTotal * (1 - r.rate)).toFixed(2);
        return [
          esc(r.name),
          '',
          esc(r.email || ''),
          esc(r.mobile_phone || ''),
          '', // Mobile
          '', // Fax
          '', // Website
          '', // Street
          '', // City
          '', // State
          '', // ZIP
          '', // Country
          payout,
          isoDate,
          '', // Tax ID
        ].join(',');
      });
    const csv = [header.join(','), ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payouts-qbo-vendors-${today.replace(/\//g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const inp = { padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, background: 'white', color: '#1f2937', width: '100%', boxSizing: 'border-box' };

  if (loading) return <div style={{ padding: 16, fontSize: 13, color: '#6b7280' }}>Loading…</div>;
  if (!rows?.length) return <div style={{ padding: 16, fontSize: 13, color: '#9ca3af' }}>No artwork sales with assigned artists found. Assign artists to orders to see payouts.</div>;

  const totalPayout = rows.reduce((s, r) => s + r.salesTotal * (1 - r.rate), 0);
  const paidCount = rows.filter((r) => r.payoutStatus === 'paid').length;

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
            <TH>Artist</TH><TH>Artwork Sales</TH><TH>Venue % (keeps)</TH><TH>Artist Payout</TH>
            <TH>Status</TH><TH>Actions</TH>
            <th style={{ padding: '8px 6px', width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const payout = row.salesTotal * (1 - row.rate);
            const isSending = !!sending[row.artistUuid];
            const isMarking = !!marking[row.artistUuid];
            const statusStyle = PAYOUT_STATUS_STYLES[row.payoutStatus] || PAYOUT_STATUS_STYLES.pending;
            return (
              <tr key={row.artistUuid} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '8px 6px', fontWeight: 500 }}>{row.name}</td>
                <td style={{ padding: '8px 6px', fontWeight: 600 }}>{fmt(row.salesTotal)}</td>
                <td style={{ padding: '8px 6px' }}>
                  {row.editing ? (
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <input
                        style={{ ...inp, width: 52 }}
                        type="number" min="0" max="100" step="1"
                        value={row.rateInput}
                        onChange={(e) => setRows((prev) => prev.map((r, i) => i === idx ? { ...r, rateInput: e.target.value } : r))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveRate(idx);
                          if (e.key === 'Escape') setRows((prev) => prev.map((r, i) => i === idx ? { ...r, editing: false, rateInput: (r.rate * 100).toFixed(0) } : r));
                        }}
                        autoFocus
                      />
                      <span style={{ fontSize: 11, color: '#6b7280' }}>%</span>
                      <button onClick={() => saveRate(idx)} disabled={row.saving}
                        style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', lineHeight: 1 }}>
                        <Check size={11} />
                      </button>
                      <button onClick={() => setRows((prev) => prev.map((r, i) => i === idx ? { ...r, editing: false, rateInput: (r.rate * 100).toFixed(0) } : r))}
                        style={{ background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', lineHeight: 1 }}>
                        <X size={11} />
                      </button>
                    </div>
                  ) : (
                    <span style={{ fontSize: 12, color: '#374151' }}>{(row.rate * 100).toFixed(0)}%</span>
                  )}
                </td>
                <td style={{ padding: '8px 6px', fontWeight: 700, color: '#7c3aed' }}>{fmt(payout)}</td>
                <td style={{ padding: '8px 6px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 9999, display: 'inline-block', whiteSpace: 'nowrap', background: statusStyle.background, color: statusStyle.color }}>
                      {statusStyle.label}
                    </span>
                    {row.emailSentAt && row.payoutStatus === 'emailed' && (
                      <span style={{ fontSize: 10, color: '#9ca3af' }}>
                        sent {fmtDate(row.emailSentAt)}
                        {row.emailOpenedAt && <span style={{ color: '#16a34a', marginLeft: 4 }}>· opened</span>}
                      </span>
                    )}
                    {row.payoutStatus === 'paid' && row.paymentConfirmedAt && (
                      <span style={{ fontSize: 10, color: '#9ca3af' }}>{fmtDate(row.paymentConfirmedAt)}</span>
                    )}
                  </div>
                </td>
                <td style={{ padding: '8px 6px' }}>
                  {row.payoutStatus !== 'paid' && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {row.email && (
                        <button
                          onClick={() => previewPayoutEmail(idx)}
                          disabled={isSending}
                          title={row.email}
                          style={{ fontSize: 11, padding: '4px 9px', borderRadius: 4, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', cursor: isSending ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 3, opacity: isSending ? 0.6 : 1 }}>
                          <DollarSign size={10} />
                          {isSending ? 'Sending…' : row.payoutStatus === 'emailed' ? 'Re-send' : 'Send Email'}
                        </button>
                      )}
                      <button
                        onClick={() => markPaid(idx)}
                        disabled={isMarking}
                        style={{ fontSize: 11, padding: '4px 9px', borderRadius: 4, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#166534', cursor: isMarking ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 3, opacity: isMarking ? 0.6 : 1 }}>
                        <Check size={10} />
                        {isMarking ? '…' : 'Mark Paid'}
                      </button>
                    </div>
                  )}
                  {row.payoutStatus === 'paid' && (
                    <span style={{ fontSize: 11, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Check size={11} /> Done
                    </span>
                  )}
                </td>
                <td style={{ padding: '8px 6px' }}>
                  {!row.editing && (
                    <button onClick={() => setRows((prev) => prev.map((r, i) => i === idx ? { ...r, editing: true } : r))}
                      style={{ background: 'transparent', border: '1px solid #e5e7eb', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>
                      <Edit size={12} />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ padding: '10px 12px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, flexWrap: 'wrap', gap: 6 }}>
        <span style={{ color: '#9ca3af' }}>
          Venue % = venue’s share · <Edit size={10} style={{ display: 'inline', verticalAlign: 'middle' }} /> edits commission
          {paidCount > 0 && <span style={{ marginLeft: 8, color: '#16a34a', fontWeight: 600 }}>{paidCount}/{rows.length} paid</span>}
        </span>
        <span style={{ fontWeight: 700, color: '#7c3aed' }}>Total payouts: {fmt(totalPayout)}</span>
        <button
          onClick={exportPayoutsCSV}
          title="Download CSV formatted for QuickBooks Bill import"
          style={{ fontSize: 11, padding: '5px 10px', borderRadius: 5, border: '1px solid #d1d5db', background: '#f9fafb', color: '#374151', cursor: 'pointer', fontWeight: 500 }}>
          Export for QBO ↓
        </button>
      </div>

      {/* One-off Email Modal */}
      {payoutPreview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={(e) => { if (e.target === e.currentTarget) setPayoutPreview(null); }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 760, boxShadow: '0 24px 64px rgba(0,0,0,0.28)', display: 'flex', flexDirection: 'column', maxHeight: '92vh', overflow: 'hidden' }}>

            {/* Header */}
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f9fafb', borderRadius: '12px 12px 0 0' }}>
              <div>
                <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>Send Email</span>
                <span style={{ marginLeft: 10, fontSize: 13, color: '#6b7280' }}>
                  To: <strong style={{ color: '#374151' }}>{payoutPreview.recipientName}</strong>
                  <span style={{ color: '#9ca3af', marginLeft: 4 }}>&#x3c;{payoutPreview.recipientEmail}&#x3e;</span>
                </span>
              </div>
              <button onClick={() => setPayoutPreview(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 20, lineHeight: 1, padding: 4 }}>&times;</button>
            </div>

            {/* Quick templates */}
            <div style={{ padding: '10px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', background: '#fff' }}>
              <span style={{ fontSize: 11, color: '#9ca3af', marginRight: 4, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Quick fill:</span>
              {payoutEmailTemplates.map((tpl) => (
                <button key={tpl.label}
                  onClick={() => {
                    const row = rows[payoutPreview.idx];
                    setPayoutPreview((p) => ({
                      ...p,
                      subject: tpl.buildSubject(row, p.payout),
                      htmlBody: tpl.buildBody(row, p.payout),
                      sendHtmlBody: tpl.buildSendBody ? tpl.buildSendBody(row, p.payout) : tpl.buildBody(row, p.payout),
                    }));
                  }}
                  style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, border: '1px solid #d1d5db', background: '#f9fafb', color: '#374151', cursor: 'pointer', fontWeight: 500 }}>
                  {tpl.label}
                </button>
              ))}
            </div>

            {/* Compose body */}
            <div style={{ padding: '16px 20px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Subject</label>
                <input
                  value={payoutPreview.subject}
                  onChange={(e) => setPayoutPreview((p) => ({ ...p, subject: e.target.value }))}
                  placeholder="Email subject…"
                  style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 10px', fontSize: 13, color: '#1f2937' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Body</label>
                <div style={{ background: '#fff', borderRadius: 6, border: '1px solid #d1d5db' }}>
                  <RichTextEditor
                    value={payoutPreview.htmlBody}
                    onChange={(html) => setPayoutPreview((p) => ({ ...p, htmlBody: html }))}
                    placeholder="Compose your email…"
                    minHeight="280px"
                    enableImageUpload={true}
                    imageFolder="email-images"
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f9fafb', borderRadius: '0 0 12px 12px' }}>
              <span style={{ fontSize: 12, color: '#9ca3af' }}>Sends via Mandrill · logged in email analytics</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setPayoutPreview(null)}
                  style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
                  Cancel
                </button>
                <button onClick={confirmSendPayoutEmail}
                  disabled={!payoutPreview.subject || !payoutPreview.htmlBody}
                  style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: !payoutPreview.subject || !payoutPreview.htmlBody ? '#d1d5db' : '#1d4ed8', color: '#fff', fontSize: 13, cursor: !payoutPreview.subject || !payoutPreview.htmlBody ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                  Send Email →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Expenses Section ─────────────────────────────────────────────────────────
const ExpensesSection = ({ collectionId, onTotalChange }) => {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addForm, setAddForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const emptyForm = () => ({ category: 'other', description: '', vendor_name: '', vendor_email: '', vendor_company: '', amount: '' });

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('collection_costs')
      .select('id, category, description, vendor_name, vendor_email, vendor_company, amount, created_at')
      .eq('collection_id', collectionId)
      .order('created_at', { ascending: false });
    setRows(data || []);
    setLoading(false);
  }, [collectionId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!rows) return;
    const total = rows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);
    if (onTotalChange) onTotalChange(total);
  }, [rows, onTotalChange]);

  const saveAdd = async () => {
    if (!addForm.description.trim() || !addForm.amount) return;
    setSaving(true);
    const { error } = await supabase.from('collection_costs').insert({
      collection_id: collectionId,
      category: addForm.category,
      description: addForm.description.trim(),
      vendor_name: addForm.vendor_name.trim() || null,
      vendor_email: addForm.vendor_email.trim() || null,
      vendor_company: addForm.vendor_company.trim() || null,
      amount: parseFloat(addForm.amount),
    });
    setSaving(false);
    if (!error) { setAddForm(null); await load(); }
    else alert('Save failed: ' + error.message);
  };

  const saveEdit = async () => {
    if (!editForm.description.trim() || !editForm.amount) return;
    setSaving(true);
    const { error } = await supabase.from('collection_costs').update({
      category: editForm.category,
      description: editForm.description.trim(),
      vendor_name: editForm.vendor_name.trim() || null,
      vendor_email: editForm.vendor_email.trim() || null,
      vendor_company: editForm.vendor_company.trim() || null,
      amount: parseFloat(editForm.amount),
    }).eq('id', editingId);
    setSaving(false);
    if (!error) { setEditingId(null); await load(); }
    else alert('Save failed: ' + error.message);
  };

  const deleteRow = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    const { error } = await supabase.from('collection_costs').delete().eq('id', id);
    if (!error) setRows((prev) => prev.filter((r) => r.id !== id));
    else alert('Delete failed: ' + error.message);
  };

  const inp = { padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, background: 'white', color: '#1f2937', width: '100%', boxSizing: 'border-box' };
  const catColor = { talent: '#8b5cf6', production: '#0ea5e9', marketing: '#f59e0b', labor: '#10b981', other: '#9ca3af' };

  const renderFormCells = (form, onChange, onSave, onCancel) => (
    <>
      <td style={{ padding: '6px' }}>
        <select style={inp} value={form.category} onChange={(e) => onChange({ ...form, category: e.target.value })}>
          {COST_CATEGORIES.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
        </select>
      </td>
      <td style={{ padding: '6px' }}>
        <input style={inp} value={form.description} placeholder="Description *" onChange={(e) => onChange({ ...form, description: e.target.value })} />
      </td>
      <td style={{ padding: '6px' }}>
        <input style={inp} value={form.vendor_name} placeholder="Vendor name" onChange={(e) => onChange({ ...form, vendor_name: e.target.value })} />
      </td>
      <td style={{ padding: '6px' }}>
        <input style={{ ...inp, width: 90 }} type="number" step="0.01" min="0" value={form.amount} placeholder="0.00 *" onChange={(e) => onChange({ ...form, amount: e.target.value })} />
      </td>
      <td style={{ padding: '6px' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={onSave} disabled={saving}
            style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: 4, padding: '4px 8px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 3 }}>
            <Check size={11} /> {saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={onCancel}
            style={{ background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 3 }}>
            <X size={11} />
          </button>
        </div>
      </td>
    </>
  );

  if (loading) return <div style={{ padding: 16, fontSize: 13, color: '#6b7280' }}>Loading…</div>;

  const total = (rows || []).reduce((s, r) => s + parseFloat(r.amount || 0), 0);
  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
              <TH>Category</TH><TH>Description</TH><TH>Vendor</TH><TH>Amount</TH>
              <th style={{ padding: '8px 6px', width: 90 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows?.map((row) =>
              editingId === row.id ? (
                <tr key={row.id} style={{ background: '#f0fdf4' }}>
                  {renderFormCells(editForm, setEditForm, saveEdit, () => setEditingId(null))}
                </tr>
              ) : (
                <tr key={row.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '8px 6px' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 9999, background: (catColor[row.category] || '#9ca3af') + '22', color: catColor[row.category] || '#6b7280' }}>
                      {row.category}
                    </span>
                  </td>
                  <td style={{ padding: '8px 6px' }}>{row.description}</td>
                  <td style={{ padding: '8px 6px', color: '#6b7280', fontSize: 12 }}>{row.vendor_name || '—'}</td>
                  <td style={{ padding: '8px 6px', fontWeight: 600 }}>{fmt(row.amount)}</td>
                  <td style={{ padding: '8px 6px' }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => { setEditingId(row.id); setEditForm({ category: row.category, description: row.description, vendor_name: row.vendor_name || '', vendor_email: row.vendor_email || '', vendor_company: row.vendor_company || '', amount: row.amount?.toString() || '' }); }}
                        style={{ background: 'transparent', border: '1px solid #e5e7eb', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>
                        <Edit size={12} />
                      </button>
                      <button onClick={() => deleteRow(row.id)}
                        style={{ background: 'transparent', border: '1px solid #fca5a5', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', color: '#dc2626', lineHeight: 1 }}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            )}
            {rows?.length === 0 && !addForm && (
              <tr>
                <td colSpan={5} style={{ padding: '16px', textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>No expenses recorded. Click “Add Expense” to start.</td>
              </tr>
            )}
            {addForm && (
              <tr style={{ background: '#f0fdf4' }}>
                {renderFormCells(addForm, setAddForm, saveAdd, () => setAddForm(null))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div style={{ padding: '10px 12px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {!addForm ? (
          <button onClick={() => setAddForm(emptyForm())}
            style={{ background: 'white', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Plus size={13} /> Add Expense
          </button>
        ) : <span />}
        <span style={{ fontWeight: 700, color: '#dc2626' }}>Total: {fmt(total)}</span>
      </div>
    </div>
  );
};

// ── Main tab ─────────────────────────────────────────────────────────────────
const FinancialSummaryTab = ({ collectionId, collectionEvents = [], submissionDetails }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [artistPayoutsTotal, setArtistPayoutsTotal] = useState(0);
  const [expensesTotal, setExpensesTotal] = useState(0);

  const eventIds = (collectionEvents || []).map((e) => e.id).filter(Boolean);
  const toggle = (key) => setExpanded((prev) => (prev === key ? null : key));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let byType = {};
      if (eventIds.length > 0) {
        const { data: orders, error: ordErr } = await supabase
          .from('orders').select('product_type, price').in('event_id', eventIds).neq('fulfillment_status', 'cancelled');
        if (ordErr) throw ordErr;
        for (const o of orders || []) {
          const t = o.product_type || 'unknown';
          byType[t] = byType[t] || { count: 0, revenue: 0 };
          byType[t].count++;
          byType[t].revenue += parseFloat(o.price || 0);
        }
      }

      let preMerch = { count: 0, revenue: 0 };
      if (eventIds.length > 0) {
        const { data: mo } = await supabase.from('event_merch_orders').select('total_amount').in('event_id', eventIds).eq('payment_status', 'paid');
        if (mo) preMerch = { count: mo.length, revenue: mo.reduce((s, r) => s + parseFloat(r.total_amount || 0), 0) };
      }

      const ticketRevenue = collectionEvents.reduce((s, e) => s + (e.total_revenue || 0), 0);
      const ticketCount = collectionEvents.reduce((s, e) => s + (e.tickets_sold || 0), 0);

      let submissions = { count: 0, revenue: 0, feePerSubmission: 0, hasUnpaid: false, unpaidCount: 0 };
      const feeAmt = parseFloat(submissionDetails?.submission_price || 0);
      if (feeAmt > 0) {
        const { data: subRows } = await supabase.from('submissions_details').select('submission_id').eq('collection_id', collectionId);
        if (subRows?.length) {
          const ids = subRows.map((r) => r.submission_id).filter(Boolean);
          if (ids.length) {
            const [{ count: paidCount }, { count: unpaidCount }] = await Promise.all([
              supabase.from('artist_submission_mapping').select('*', { count: 'exact', head: true }).in('submission_id', ids).eq('paid', true),
              supabase.from('artist_submission_mapping').select('*', { count: 'exact', head: true }).in('submission_id', ids).eq('paid', false),
            ]);
            submissions = { count: paidCount || 0, revenue: (paidCount || 0) * feeAmt, feePerSubmission: feeAmt, hasUnpaid: (unpaidCount || 0) > 0, unpaidCount: unpaidCount || 0 };
          }
        }
      }

      setData({
        originals: byType['original'] || { count: 0, revenue: 0 },
        prints: byType['print'] || { count: 0, revenue: 0 },
        merch: { count: (byType['merch']?.count || 0) + preMerch.count, revenue: (byType['merch']?.revenue || 0) + preMerch.revenue },
        tickets: { count: ticketCount, revenue: ticketRevenue },
        submissions,
        eventCount: eventIds.length,
      });
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [collectionId, collectionEvents, submissionDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>
      <RefreshCw size={24} style={{ marginBottom: 8 }} /><div>Loading financials\u2026</div>
    </div>
  );

  if (error) return (
    <div style={{ padding: 24, display: 'flex', gap: 10, alignItems: 'flex-start', color: '#dc2626', background: '#fef2f2', borderRadius: 10, margin: 16 }}>
      <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
      <div>
        <div style={{ fontWeight: 600 }}>Failed to load financials</div>
        <div style={{ fontSize: 13, marginTop: 4 }}>{error}</div>
        <button onClick={load} style={{ marginTop: 10, padding: '6px 14px', background: 'white', border: '1px solid #fca5a5', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#dc2626' }}>Retry</button>
      </div>
    </div>
  );

  const totalRevenue = data.originals.revenue + data.prints.revenue + data.merch.revenue + data.tickets.revenue + data.submissions.revenue;
  const totalSales = data.originals.count + data.prints.count + data.merch.count + data.tickets.count + data.submissions.count;
  const hasAnyRevenue = totalRevenue > 0;

  return (
    <div style={{ padding: '20px 16px', maxWidth: 760 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1f2937' }}>Revenue Summary</h3>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
            {data.eventCount > 0 ? `Across ${data.eventCount} event${data.eventCount !== 1 ? 's' : ''} \u00b7 click any row to expand` : 'No events linked yet'}
          </p>
        </div>
        <button onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 13px', background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', fontSize: 13, color: '#374151' }}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {!hasAnyRevenue ? (
        <div style={{ textAlign: 'center', padding: '48px 24px', color: '#9ca3af', background: '#f9fafb', borderRadius: 12, border: '1px dashed #e5e7eb' }}>
          <TrendingUp size={32} style={{ marginBottom: 12, opacity: 0.4 }} />
          <div style={{ fontWeight: 600, marginBottom: 6 }}>No revenue recorded yet</div>
          <div style={{ fontSize: 13 }}>Sales from artwork, prints, merch, tickets, and submission fees will appear here.</div>
        </div>
      ) : null}

      {eventIds.length > 0 && (
        <>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Artwork Sales</div>
            <Section icon={Image} label="Original Artwork" count={data.originals.count} revenue={data.originals.revenue} color="#8b5cf6" expanded={expanded === 'originals'} onToggle={() => toggle('originals')}>
              <OrdersDetail eventIds={eventIds} productType="original" onDataChange={load} />
            </Section>
            <Section icon={Printer} label="Prints" count={data.prints.count} revenue={data.prints.revenue} color="#0ea5e9" expanded={expanded === 'prints'} onToggle={() => toggle('prints')}>
              <OrdersDetail eventIds={eventIds} productType="print" onDataChange={load} />
            </Section>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Merchandise</div>
            <Section icon={ShoppingBag} label="Merch / Event Items" count={data.merch.count} revenue={data.merch.revenue} color="#f59e0b" sublabel="Terminal sales + pre-orders" expanded={expanded === 'merch'} onToggle={() => toggle('merch')}>
              <MerchDetail eventIds={eventIds} onDataChange={load} />
            </Section>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Event Tickets</div>
            <Section icon={Ticket} label="Ticket Sales" count={data.tickets.count} revenue={data.tickets.revenue} color="#10b981" expanded={expanded === 'tickets'} onToggle={() => toggle('tickets')}>
              <TicketsDetail collectionEvents={collectionEvents} eventIds={eventIds} onDataChange={load} />
            </Section>
          </div>
          {(data.submissions.count > 0 || submissionDetails?.submission_price > 0) && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Call-Out Entry Fees</div>
              <Section
                icon={FileText} label="Submission Fees" count={data.submissions.count} revenue={data.submissions.revenue} color="#6366f1"
                sublabel={data.submissions.hasUnpaid ? `${fmt(data.submissions.feePerSubmission)} per entry \u00b7 ${data.submissions.unpaidCount} unpaid not included` : `${fmt(data.submissions.feePerSubmission)} per entry`}
                expanded={expanded === 'submissions'} onToggle={() => toggle('submissions')}>
                <SubmissionsDetail collectionId={collectionId} submissionDetails={submissionDetails} onDataChange={load} />
              </Section>
            </div>
          )}
          {hasAnyRevenue && (
            <>
              <div style={{ marginTop: 20, padding: '16px 18px', background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 700, fontSize: 15, color: '#1f2937' }}>Total Revenue</span>
                <span style={{ fontWeight: 800, fontSize: 20, color: '#16a34a' }}>{fmt(totalRevenue)}</span>
              </div>
              <div style={{ marginTop: 6, textAlign: 'right', fontSize: 12, color: '#9ca3af' }}>{totalSales} total transactions</div>
            </>
          )}
        </>
      )}
      {/* ── Artist Payouts ── */}
      <div style={{ marginTop: 24, marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Artist Payouts</div>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, background: 'white', overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', background: '#faf5ff', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: '#1f2937' }}>Artist Payouts</span>
            <span style={{ fontWeight: 700, fontSize: 15, color: '#7c3aed' }}>{fmt(artistPayoutsTotal)}</span>
          </div>
          <ArtistPayoutsSection collectionId={collectionId} eventIds={eventIds} onTotalChange={setArtistPayoutsTotal} />
        </div>
      </div>

      {/* ── Expenses ── */}
      <div style={{ marginTop: 16, marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Expenses</div>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, background: 'white', overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', background: '#fff5f5', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: '#1f2937' }}>Expenses & Costs</span>
            <span style={{ fontWeight: 700, fontSize: 15, color: '#dc2626' }}>{fmt(expensesTotal)}</span>
          </div>
          <ExpensesSection collectionId={collectionId} onTotalChange={setExpensesTotal} />
        </div>
      </div>

      {/* ── P&L Summary ── */}
      <div style={{ marginTop: 20, border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ padding: '12px 18px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#1f2937' }}>P&L Summary</span>
        </div>
        <div style={{ padding: '4px 18px 12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
            <span style={{ color: '#374151' }}>Total Revenue</span>
            <span style={{ fontWeight: 600, color: '#16a34a' }}>+ {fmt(totalRevenue)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
            <span style={{ color: '#374151' }}>Artist Payouts</span>
            <span style={{ fontWeight: 600, color: '#7c3aed' }}>− {fmt(artistPayoutsTotal)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e5e7eb', fontSize: 13 }}>
            <span style={{ color: '#374151' }}>Expenses & Costs</span>
            <span style={{ fontWeight: 600, color: '#dc2626' }}>− {fmt(expensesTotal)}</span>
          </div>
          {(() => {
            const net = totalRevenue - artistPayoutsTotal - expensesTotal;
            return (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0 4px', fontSize: 16 }}>
                <span style={{ fontWeight: 700, color: '#1f2937' }}>Net P&L</span>
                <span style={{ fontWeight: 800, color: net >= 0 ? '#16a34a' : '#dc2626' }}>
                  {net >= 0 ? '' : '− '}{fmt(Math.abs(net))}
                </span>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default FinancialSummaryTab;
